import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  GridColumn,
  Heading,
  Select,
} from '@walmart-web/livingdesign-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './acntMetricsDashboard.scss';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { CircularProgress } from '@mui/material';
import ACNTService from '../../service';
import { useMutation } from 'react-query';
import moment, { Moment } from 'moment';
import { IAcntApplicationCountMetrics, IPaymentStatusMetrics } from '../../types/serviceTypes';
import { GrantType } from '../../constants';

interface AcntMetricsCardProps {
  title: string;
  loadingFlag: boolean;
  data: number;
}

const AcntMetricsDashboard = () => {
  const { t } = useTranslation();
  const applicationDataMetrics = useMutation('applicationDataMetrics', ACNTService.ApplicationCountMetrics);
  const resubmittedApplicationCount = useMutation(
    'resubmittedApplicationCount',
    ACNTService.ResubmittedApplicationCount
  );
  const paymentStatusCount = useMutation('paymentStatusCount', ACNTService.PaymentStatusCount);
  const activeUserCount = useMutation('activeUserCount', ACNTService.AllActiveUser);

  const [startDate, setStartDate] = useState<Moment | null>(
    moment(new Date(new Date().setMonth(new Date().getMonth() - 1)))
  );
  const [endDate, setEndDate] = useState<Moment | null>(moment(new Date()));
  const [grantType, setGrantType] = useState<GrantType>(GrantType.ALL);

  const [appData, setAppData] = useState<IAcntApplicationCountMetrics>();
  const [resubmitData, setResubmitData] = useState<number>(0);
  const [paymentStatusData, setPaymentStatusData] = useState<IPaymentStatusMetrics>();
  const [activeUser, setActiveUser] = useState<number>();

  useEffect(() => {
    handleCountLoading();
  }, []);

  const handleApplicationCountMetrics = (setAppData) => (data) => {
    if (data) {
      setAppData(data);
    }
  };

  const handleResubmittedApplicationCount = (setResubmitData) => (data) => {
    if (data) {
      setResubmitData(data);
    }
  };

  const hanldePaymentStatusCount = (setPaymentStatusData) => (data) => {
    if (data) {
      setPaymentStatusData(data);
    }
  };

  const handleActiveUserCount = (setActiveUser) => (data) => {
    if (data) {
      setActiveUser(data);
    }
  };

  const handleCountLoading = () => {
    if (startDate && endDate) {
      const payload: {
        grantType?: GrantType;
        startDate: string;
        endDate: string;
      } = {
        grantType: grantType || undefined,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      };
      if (grantType === 'ALL') {
        delete payload['grantType'];
      }
      applicationDataMetrics.mutate(payload, {
        onSuccess: handleApplicationCountMetrics(setAppData),
      });

      resubmittedApplicationCount.mutate(payload, {
        onSuccess: handleResubmittedApplicationCount(setResubmitData),
      });

      paymentStatusCount.mutate(payload, { onSuccess: hanldePaymentStatusCount(setPaymentStatusData) });

      activeUserCount.mutate(payload, { onSuccess: handleActiveUserCount(setActiveUser) });
    }
  };

  return (
    <div className="acnt-metrics-container">
      <div className="heading">
        <Heading UNSAFE_className="title">{t('acntMetrics.title')}</Heading>
      </div>
      <div>
        <Grid className="grid-container-metrics">
          <GridColumn sm={12} md={4} lg={4}>
            <Select
              label={t('acntMetrics.grantType')}
              onChange={(event) => setGrantType(event.target.value as GrantType)}
            >
              <option value="ALL">{t('acntMetrics.all')}</option>
              <option value="ASSOCIATE">{t('acntMetrics.associate')}</option>
              <option value="DISASTER">{t('acntMetrics.disaster')}</option>
            </Select>
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4} className="date-rate-row">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={t('acntMetrics.startDate')}
                value={startDate}
                onChange={(event) => {
                  setStartDate(event);
                }}
                sx={{ width: '100%' }}
                format="MM-DD-YYYY"
              />
            </LocalizationProvider>
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4} className="date-rate-row">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={t('acntMetrics.endDate')}
                value={endDate}
                onChange={(event) => setEndDate(event)}
                sx={{ width: '100%' }}
                format="MM-DD-YYYY"
              />
            </LocalizationProvider>
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <Button variant="primary" className="apply-changes-button" onClick={handleCountLoading}>
              {t('acntMetrics.applyChanges')}
            </Button>
          </GridColumn>
        </Grid>
      </div>
      <div>
        <Grid hasGutter className="metrics-card">
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.activeUsers')}
              loadingFlag={activeUserCount.isLoading}
              data={activeUser || 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.totalApplications')}
              loadingFlag={applicationDataMetrics.isLoading}
              data={appData?.totalApplicationsCount ? appData?.totalApplicationsCount : 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.applicationsSubmitted')}
              loadingFlag={applicationDataMetrics.isLoading}
              data={appData?.submittedApplicationsCount ? appData?.submittedApplicationsCount : 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.applicationsResubmitted')}
              loadingFlag={applicationDataMetrics.isLoading}
              data={resubmitData || 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.applicationsReturned')}
              loadingFlag={applicationDataMetrics.isLoading}
              data={appData?.returnedApplicationCount ? appData?.returnedApplicationCount : 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.applicationsApproved')}
              loadingFlag={applicationDataMetrics.isLoading}
              data={appData?.approvedApplicationsCount ? appData?.approvedApplicationsCount : 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.paymentsPaid')}
              loadingFlag={paymentStatusCount.isLoading}
              data={paymentStatusData?.paidPayments ? paymentStatusData?.paidPayments : 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.paymentsNotInitiated')}
              loadingFlag={paymentStatusCount.isLoading}
              data={paymentStatusData?.notInitiatedPayments ? paymentStatusData?.notInitiatedPayments : 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4}>
            <AcntMetricsCard
              title={t('acntMetrics.rejectedPayments')}
              loadingFlag={paymentStatusCount.isLoading}
              data={paymentStatusData?.rejectedPayments ? paymentStatusData?.rejectedPayments : 0}
            />
          </GridColumn>
          <GridColumn sm={12} md={4} lg={4} className="last-card-position">
            <AcntMetricsCard
              title={t('acntMetrics.applicationsRejected')}
              loadingFlag={paymentStatusCount.isLoading}
              data={appData?.rejectedApplicationsCount || 0}
            />
          </GridColumn>
        </Grid>
      </div>
    </div>
  );
};

const AcntMetricsCard: React.FC<AcntMetricsCardProps> = ({ title, loadingFlag, data }) => {
  return (
    <Card size="small" className="card-dimensions">
      <React.Fragment>
        <CardHeader title={title} className="card-dimensions-header" />
        <CardContent className="card-dimensions-content">{loadingFlag ? <CircularProgress /> : data}</CardContent>
      </React.Fragment>
    </Card>
  );
};

export default AcntMetricsDashboard;
